import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
// components
import Label from '../../components/Label';
import SvgIconStyle from '../../components/SvgIconStyle';
import AddBoxIcon from '@mui/icons-material/AddBox';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';

import { useRole, useRoleGroup } from 'lib/evi-role-action/hoc';
import useLocales from '../../hooks/useLocales';
import useAuthorityAll from '../../hooks/useAuthorityAll';
import useSubjectOptions from '../../hooks/useSubjectOptions';
import { getSubjectDisplayName } from '../../api/taxonomy';
import { createNewQuestionDraft } from '../../pages/dashboard/questionBankQuestions/createNewQuestionDraft';
// ----------------------------------------------------------------------

const getIcon = (name: string) => (
  <SvgIconStyle src={`/static/icons/navbar/${name}.svg`} sx={{ width: '100%', height: '100%' }} />
);

const ICONS = {
  blog: getIcon('ic_blog'),
  cart: getIcon('ic_cart'),
  chat: getIcon('ic_chat'),
  mail: getIcon('ic_mail'),
  user: getIcon('ic_user'),
  kanban: getIcon('ic_kanban'),
  banking: getIcon('ic_banking'),
  calendar: getIcon('ic_calendar'),
  ecommerce: getIcon('ic_ecommerce'),
  analytics: getIcon('ic_analytics'),
  dashboard: getIcon('ic_dashboard'),
  booking: getIcon('ic_booking'),

  platform_resource: getIcon('ic_platform_resource'),
  album: getIcon('ic_album'),
  school_resource: getIcon('ic_school_resource'),
  bill: getIcon('ic_bill'),
  course: getIcon('ic_course'),
  aim: getIcon('ic_aim'),
  fixed: getIcon('ic_fixed'),
  statistics: getIcon('ic_statistics'),
  video: getIcon('ic_video'),

  computer: getIcon('ic_computer'),
  setting: getIcon('ic_setting'),
  service: getIcon('ic_service'),
  home: getIcon('ic_home'),
  parentChild: getIcon('ic_parent_child_task'),
  ekinder: getIcon('ic_ekinder'),
  evi_family: getIcon('ic_Evi_family'),
  customService: getIcon('ic_custom_service'),
  schoolSetting: getIcon('ic_school_setting'),
  writeSuggestion: getIcon('ic_write_suggestion'),
  aiNotes: getIcon('ic_ai'),
  tools: getIcon('ic_tools'),
  lesson_plan_tools: getIcon('ic_lesson_plan_tools'),
  permission: getIcon('ic_permission'),
  teacher: getIcon('ic_teacher')
};

const SidebarConfig = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const creatingNewQuestionRef = useRef(false);
  const resource_course = useAuthorityAll({ ctrl: 'resource_course', role: ['get_list'] });
  const resource_course_new = useAuthorityAll({ ctrl: 'resource_course_new', role: ['get_list'] });
  const resource_home_page = useAuthorityAll({ ctrl: 'resource_home_page', role: ['get_list'] });
  const resource_parent_child = useAuthorityAll({
    ctrl: 'resource_parent_child',
    role: ['get_list']
  });

  const resource_wonder_wall = useAuthorityAll({
    ctrl: 'resource_wonder_wall',
    role: ['get_list']
  });

  const resource_resource_hub = useAuthorityAll({
    ctrl: 'resource_resource_hub',
    role: ['get_list']
  });

  const resource_folder = useAuthorityAll({
    ctrl: 'resource_folder',
    role: ['get_list']
  });

  const extra_folder = useAuthorityAll({
    ctrl: 'extra_folder',
    role: ['get_list']
  });

  const main_resource = useAuthorityAll({
    ctrl: 'main_resource',
    role: ['get_list']
  });

  const resource_staff = useAuthorityAll({
    ctrl: 'resource_staff',
    role: ['get_list']
  });

  const resource_role_group = useAuthorityAll({
    ctrl: 'resource_role_group',
    role: ['get_list']
  });

  const { trans } = useLocales(); // 添加多语言设置

  // 題目列表 subject sub-nav — mirrors the in-page 學科 filter (same taxonomy
  // source). When there are more than 3 subjects, collapse to 全部學科 + top-3
  // + a 更多/收起 toggle so the sidebar stays short.
  const subjectOptions = useSubjectOptions();
  const [showAllSubjects, setShowAllSubjects] = useState(false);
  const QUESTIONS_PATH = PATH_DASHBOARD.general.internalQuestionBank.questions;
  const SUBJECT_LIMIT = 3;
  // path keeps the canonical API subject (e.g. math); title is the UI label.
  const allSubjectChildren = subjectOptions.map((name) => ({
    title: getSubjectDisplayName(name),
    path: `${QUESTIONS_PATH}?subject=${encodeURIComponent(name)}`,
    inset: true
  }));
  const subjectCollapsed = !showAllSubjects && allSubjectChildren.length > SUBJECT_LIMIT;
  const questionSubjectChildren = [
    { title: trans('dashboard.title.allSubjects'), path: QUESTIONS_PATH, inset: true },
    ...(subjectCollapsed ? allSubjectChildren.slice(0, SUBJECT_LIMIT) : allSubjectChildren),
    ...(allSubjectChildren.length > SUBJECT_LIMIT
      ? [
          {
            title: subjectCollapsed
              ? trans('dashboard.title.showMore')
              : trans('dashboard.title.showLess'),
            path: '',
            isAction: true,
            inset: true,
            onClick: () => setShowAllSubjects((value) => !value)
          }
        ]
      : [])
  ];
  const createNewQuestionFromSidebar = async () => {
    if (creatingNewQuestionRef.current) return;
    creatingNewQuestionRef.current = true;
    try {
      const question = await createNewQuestionDraft();
      const adapterId = question.adapter_id || question.id;
      navigate(
        adapterId
          ? `${PATH_DASHBOARD.general.internalQuestionBank.questions}/${adapterId}`
          : PATH_DASHBOARD.general.internalQuestionBank.newQuestion
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('questionBankQuestions.errors.tryAgain');
      enqueueSnackbar(t('questionBankQuestions.errors.createDraftFailed', { message }), {
        variant: 'error'
      });
    } finally {
      creatingNewQuestionRef.current = false;
    }
  };

  const hasAccountMenu = resource_staff?.get_list || resource_role_group?.get_list;

  const accountMenu = hasAccountMenu
    ? {
        subheader: trans('dashboard.title.accout_management'),
        items: [
          {
            title: trans('account.FacultyList'),
            path: PATH_DASHBOARD.account.root,
            notPermission: !resource_staff?.get_list,
            icon: ICONS.teacher
          },
          {
            title: trans('PermissionSetting.PermissionSetting'),
            path: PATH_DASHBOARD.account.authority,
            notPermission: !resource_role_group?.get_list,
            icon: ICONS.permission
          }
        ]
      }
    : null;

  //console.log('sss', resource_course?.get_list);
  return [
    // GENERAL
    // ----------------------------------------------------------------------
    {
      subheader: trans('dashboard.subHeader.manage'),
      items: [
        {
          title: trans('dashboard.title.homePage'),
          path: PATH_DASHBOARD.general.home,
          icon: ICONS.home,
          notPermission: !resource_home_page?.get_list
        },
        // {
        //   title: trans('dashboard.title.app'),
        //   path: PATH_DASHBOARD.general.app,
        //   icon: ICONS.dashboard,
        //   notPermission: false
        // }, //prepro收起主頁
        {
          title: trans('dashboard.title.curriculumActivities'),
          path: PATH_DASHBOARD.general.curriculumActivities,
          notPermission: !resource_course_new?.get_list,
          icon: ICONS.course
        },
        {
          title: trans('dashboard.title.schoolCourse'),
          path: PATH_DASHBOARD.general.schoolBased,
          notPermission: !resource_course?.get_list,
          icon: ICONS.course
        },
        {
          title: trans('dashboard.title.parent_child_task'),
          path: PATH_DASHBOARD.parentChildTask.list,
          notPermission: !resource_parent_child?.get_list,
          icon: ICONS.parentChild
        },
        {
          title: trans('dashboard.title.exhibition'),
          path: PATH_DASHBOARD.general.Exhibition.list,
          notPermission: !resource_wonder_wall?.get_list,
          icon: ICONS.fixed
        },
        // {
        //   title: trans('dashboard.title.platform_resource_lib'),
        //   path: PATH_DASHBOARD.general.ecommerce,
        //   notPermission: !resource_resource_hub?.get_list,
        //   icon: ICONS.platform_resource
        // },
        {
          title: trans('dashboard.title.platform_resource_lib'),
          path: PATH_DASHBOARD.general.platformResourceLibrary,
          notPermission: !resource_resource_hub?.get_list,
          icon: ICONS.platform_resource
        },
        {
          title: trans('dashboard.title.school_resource_lib'),
          path: PATH_DASHBOARD.general.schoolResource,
          notPermission: !resource_folder?.get_list,
          icon: ICONS.school_resource
        },
        {
          title: trans('dashboard.title.addition_resource_lib'),
          path: PATH_DASHBOARD.general.additionResource,
          notPermission: !extra_folder?.get_list,
          icon: <AddBoxIcon />
        },
        {
          title: trans('dashboard.title.internal_question_bank_platform'),
          path: PATH_DASHBOARD.general.internalQuestionBank.dashboard,
          notPermission: !main_resource?.get_list,
          icon: ICONS.platform_resource,
          children: [
            {
              title: trans('dashboard.title.internal_question_bank_dashboard'),
              path: PATH_DASHBOARD.general.internalQuestionBank.dashboard,
              icon: <DashboardOutlinedIcon sx={{ fontSize: 20 }} />
            },
            {
              title: trans('dashboard.title.internal_question_bank_questions'),
              path: PATH_DASHBOARD.general.internalQuestionBank.questions,
              icon: <ViewListOutlinedIcon sx={{ fontSize: 20 }} />,
              // 學科 (subject) sub-navigation — dynamic, mirrors the in-page
              // 學科 filter (same taxonomy source). Each links ?subject=<name>
              // which the list page reads into subjectFilter. Collapses to
              // 全部學科 + top-3 + 更多/收起 when there are >3 subjects.
              children: questionSubjectChildren
            },
            {
              title: trans('dashboard.title.internal_question_bank_new_question'),
              path: PATH_DASHBOARD.general.internalQuestionBank.newQuestion,
              icon: <AddOutlinedIcon sx={{ fontSize: 20 }} />,
              onClick: () => void createNewQuestionFromSidebar()
            },
            {
              title: trans('dashboard.title.internal_question_bank_chinese_word_bank'),
              path: PATH_DASHBOARD.general.internalQuestionBank.chineseWordBank,
              icon: <MenuBookOutlinedIcon sx={{ fontSize: 20 }} />
            },
            {
              title: trans('dashboard.title.internal_question_bank_taxonomy'),
              path: PATH_DASHBOARD.general.internalQuestionBank.taxonomy,
              icon: <AccountTreeOutlinedIcon sx={{ fontSize: 20 }} />
            }
          ]
        },
        {
          title: trans('dashboard.title.aplus_question_bank'),
          path: PATH_DASHBOARD.general.aplusQuestionBank.root,
          notPermission: !main_resource?.get_list,
          icon: <QuizOutlinedIcon />,
          children: [
            {
              title: trans('dashboard.title.aplus_question_bank_questions'),
              path: PATH_DASHBOARD.general.aplusQuestionBank.questions,
              icon: <ViewListOutlinedIcon sx={{ fontSize: 20 }} />
            },
            {
              title: trans('dashboard.title.aplus_question_bank_new_question'),
              path: PATH_DASHBOARD.general.aplusQuestionBank.newQuestion,
              icon: <AddOutlinedIcon sx={{ fontSize: 20 }} />
            },
            {
              title: trans('dashboard.title.aplus_question_bank_bulk_import'),
              path: PATH_DASHBOARD.general.aplusQuestionBank.bulkImport,
              icon: <UploadFileOutlinedIcon sx={{ fontSize: 20 }} />
            },
            {
              title: trans('dashboard.title.aplus_campaign'),
              path: PATH_DASHBOARD.general.aplusCampaign.root,
              icon: <EventNoteOutlinedIcon sx={{ fontSize: 20 }} />,
              children: [
                {
                  title: trans('dashboard.title.aplus_campaign_list'),
                  path: PATH_DASHBOARD.general.aplusCampaign.root,
                  inset: true
                },
                {
                  title: trans('dashboard.title.aplus_campaign_new'),
                  path: PATH_DASHBOARD.general.aplusCampaign.newCampaign,
                  inset: true
                }
              ]
            }
          ]
        }
        // {
        //   title: trans('dashboard.title.platform_resource_lib'),
        //   path: PATH_DASHBOARD.general.platformResourceLibrary,
        //   notPermission: !resource_resource_hub?.get_list,
        //   icon: ICONS.platform_resource
        // }
        // {
        //   title: trans('dashboard.title.album'),
        //   path: PATH_DASHBOARD.general.analytics,
        //   icon: ICONS.album
        // },
        // {
        //   title: trans('dashboard.title.schoolResourceList'),
        //   path: PATH_DASHBOARD.general.SchoolResourceList,
        //   icon: ICONS.school_resource
        // },
        // {
        //   title: trans('dashboard.title.custom_list'),
        //   path: PATH_DASHBOARD.general.customList,
        //   icon: ICONS.bill
        // },
        // {
        //   title: trans('dashboard.title.search'),
        //   path: PATH_DASHBOARD.general.SmallActivity,
        //   icon: ICONS.aim
        // },
        // {
        //   title: trans('dashboard.title.student_count'),
        //   path: PATH_DASHBOARD.general.booking,
        //   icon: ICONS.statistics
        // },
        // {
        //   title: trans('dashboard.title.inline_course'),
        //   path: PATH_DASHBOARD.calendar,
        //   icon: ICONS.video
        // }
      ]
    },
    accountMenu,

    // 其他
    {
      subheader: trans('dashboard.subHeader.other'),
      items: [
        {
          title: trans('dashboard.title.ekinder'),
          path: '#',
          src: 'https://www.ekinder.com.hk',
          icon: (
            <span style={{ width: 28, height: 28 }}>
              <img alt="" src="/static/home/ekinder.svg" />
            </span>
          )
        },
        {
          title: 'EVIGARTEN',
          path: '#',
          src: 'https://www.evigarten.com',
          icon: (
            <span style={{ width: 28, height: 28 }}>
              <img alt="" src="/static/home/evigarten.png" />
            </span>
          )
        },
        {
          title: trans('dashboard.title.evi_family'),
          src: 'https://hk.evi.com.hk/school',
          path: '#',
          icon: (
            <span style={{ width: 28, height: 28 }}>
              <img alt="" src="/static/home/evi-family.svg" />
            </span>
          )
        }
        // {
        //   title: trans('dashboard.title.lesson_plan_tools'),
        //   path: PATH_DASHBOARD.general.lessonPlanTools,
        //   icon: (
        //     <span
        //       style={{
        //         width: 28,
        //         height: 28,
        //         display: 'flex',
        //         justifyContent: 'center',
        //         alignItems: 'center'
        //       }}
        //     >
        //       <img alt="" src="/static/icons/navbar/ic_lesson_plan_tools.svg" />
        //     </span>
        //   ) //prepro_hidden
        // }

        // {
        //   title: trans('dashboard.title.AiNotes'),
        //   path: '#',
        //   icon: ICONS.aiNotes
        // },
        // {
        //   title: trans('dashboard.title.tools'),
        //   path: '#',
        //   icon: ICONS.tools
        // }, //prepro收起coming soon item
        // {
        //   title: trans('dashboard.title.schoolSetting'),
        //   path: '#',
        //   icon: ICONS.schoolSetting
        // },
        // {
        //   title: trans('dashboard.title.writeSuggestion'),
        //   path: '#',
        //   icon: ICONS.writeSuggestion
        // } //prepro收起coming soon item
      ]
    }
    // {
    //   subheader: trans('dashboard.title.accout_management'),
    //   items: [
    //     {
    //       title: trans('account.FacultyList'),
    //       // notPermission: !nsStaffCtrl.get_list,
    //       path: PATH_DASHBOARD.account.root,
    //       notPermission: !resource_staff?.get_list,

    //       icon: ICONS.teacher
    //     },
    //     {
    //       title: trans('PermissionSetting.PermissionSetting'),
    //       // notPermission: !nsRoleGroup.get_list,
    //       path: PATH_DASHBOARD.account.authority,
    //       notPermission: !resource_role_group?.get_list,
    //       icon: ICONS.permission
    //     }
    //   ]
    // }

    // MANAGEMENT
    // // ----------------------------------------------------------------------
    // {
    //   subheader: trans('dashboard.subHeader.other'),
    //   items: [
    //     // MANAGEMENT : USER
    //     {
    //       title: trans('dashboard.title.intranet'),
    //       path: PATH_DASHBOARD.user.root,
    //       icon: ICONS.user,
    //       children: [
    //         { title: trans('dashboard.title.profile'), path: PATH_DASHBOARD.user.profile },
    //         { title: trans('dashboard.title.card'), path: PATH_DASHBOARD.user.cards },
    //         { title: trans('dashboard.title.list'), path: PATH_DASHBOARD.user.list },
    //         { title: trans('dashboard.title.create'), path: PATH_DASHBOARD.user.newUser },
    //         { title: trans('dashboard.title.edit'), path: PATH_DASHBOARD.user.editById },
    //         { title: trans('dashboard.title.account'), path: PATH_DASHBOARD.user.account }
    //       ]
    //     },

    //     {
    //       title: trans('dashboard.title.setting'),
    //       path: PATH_DASHBOARD.setting,
    //       icon: ICONS.setting
    //     },
    //     {
    //       title: trans('dashboard.title.inline_search'),
    //       path: PATH_DASHBOARD.general.booking,
    //       icon: ICONS.service
    //     },

    //     // MANAGEMENT : E-COMMERCE
    //     {
    //       title: trans('dashboard.title.e-commerce'),
    //       path: PATH_DASHBOARD.eCommerce.root,
    //       icon: ICONS.cart,
    //       children: [
    //         { title: 'shop', path: PATH_DASHBOARD.eCommerce.shop },
    //         { title: 'product', path: PATH_DASHBOARD.eCommerce.productById },
    //         { title: 'list', path: PATH_DASHBOARD.eCommerce.list },
    //         { title: 'create', path: PATH_DASHBOARD.eCommerce.newProduct },
    //         { title: 'edit', path: PATH_DASHBOARD.eCommerce.editById },
    //         { title: 'checkout', path: PATH_DASHBOARD.eCommerce.checkout },
    //         { title: 'invoice', path: PATH_DASHBOARD.eCommerce.invoice }
    //       ]
    //     },

    //     // MANAGEMENT : BLOG
    //     {
    //       title: trans('dashboard.title.blog'),
    //       path: PATH_DASHBOARD.blog.root,
    //       icon: ICONS.blog,
    //       children: [
    //         { title: 'posts', path: PATH_DASHBOARD.blog.posts },
    //         { title: 'post', path: PATH_DASHBOARD.blog.postById },
    //         { title: 'new post', path: PATH_DASHBOARD.blog.newPost }
    //       ]
    //     }
    //   ]
    // },

    // APP
    // ----------------------------------------------------------------------
    // {
    //   subheader: trans('dashboard.subHeader.app'),
    //   items: [
    //     {
    //       title: trans('dashboard.title.mail'),
    //       path: PATH_DASHBOARD.mail.root,
    //       icon: ICONS.mail,
    //       info: <Label color="error">2</Label>
    //     },
    //     { title: trans('dashboard.title.chat'), path: PATH_DASHBOARD.chat.root, icon: ICONS.chat },
    //     {
    //       title: trans('dashboard.title.calendar'),
    //       path: PATH_DASHBOARD.calendar,
    //       icon: ICONS.calendar
    //     },
    //     {
    //       title: trans('dashboard.title.kanban'),
    //       path: PATH_DASHBOARD.kanban,
    //       icon: ICONS.kanban
    //     }
    //   ]
    // }
  ].filter(Boolean);
};

export default SidebarConfig;

// export default sidebarConfig;
